import { Badge } from '../../../shared/ui/Primitives';
import type { ConfigurationSnapshot } from '../consumer/configuration-state';
import type { MonitoringReadState } from '../consumer/polling';
import { MonitoringReadNotice } from './Summary';
export const COVERAGE_TEXT = {
  NO_SOURCES: '尚未配置监测来源',
  ALL_DISABLED: '全部来源停用；覆盖缺失',
  COVERAGE_GAP: '监测覆盖不足；缺口需新鲜合格观测',
  COMPLETE: '当前三类指标覆盖完整；不代表区域安全',
};
export function MonitoringNetworkView({
  state,
  retry,
  overview = false,
}: {
  readonly state: MonitoringReadState<ConfigurationSnapshot>;
  readonly retry: () => void;
  readonly overview?: boolean;
}) {
  const data = state.data;
  return (
    <section data-nm-network data-nm-config-revision={data?.network.configRevision}>
      <h3>区域监测网络 · 配置意图与数据覆盖</h3>
      <MonitoringReadNotice state={state} retry={retry} label="监测网络" />
      {data && (
        <>
          <p className="nm-scope">
            范围：{data.configuration.instanceId} · {data.network.sourceMode} · 当前配置修订{' '}
            <span data-nm-current-config-revision>{data.network.configRevision}</span>
          </p>
          <p className="p01-muted">
            配置生成：{data.configuration.generatedAt} · 网络生成：{data.network.generatedAt}
          </p>
          {Date.now() - Date.parse(data.network.generatedAt) > 5000 && (
            <p className="nm-stale" role="status">
              网络已延迟；保留最后有效覆盖，不表示当前安全。
            </p>
          )}
          <div className="nm-metrics">
            <div>
              <span>已配置区域</span>
              <strong data-nm-configured-area-count>{data.network.areas.length}</strong>
            </div>
            <div>
              <span>已配置来源</span>
              <strong data-nm-configured-source-count>
                {data.network.areas.reduce((sum, a) => sum + a.configuredSources, 0)}
              </strong>
            </div>
            <div>
              <span>启用来源</span>
              <strong data-nm-enabled-source-count>
                {data.network.areas.reduce((sum, a) => sum + a.enabledSources, 0)}
              </strong>
            </div>
            <div>
              <span>本周期近期数据来源</span>
              <strong data-nm-recent-source-count>
                {data.network.areas.reduce((sum, a) => sum + a.recentSources, 0)}
              </strong>
            </div>
          </div>
          <p className="p01-muted">
            计数来自后端同一修订网络；RECENT 是本启用周期近 12 秒接收健康，有资格参与指标覆盖还需
            GOOD / 无 Unknown / 新鲜度。可用性是数据覆盖状态。
          </p>
          {data.network.areas.length === 0 && (
            <p className="nm-empty">尚未配置区域；监测覆盖未知。</p>
          )}
          <div className="nm-area-grid">
            {data.network.areas.map((a) => (
              <article
                className="nm-area"
                key={a.areaId}
                data-nm-coverage-area={a.areaId}
                data-nm-coverage={a.coverage}
              >
                <h3>
                  {data.configuration.areas.find((c) => c.areaId === a.areaId)?.displayName}{' '}
                  <Badge>{a.coverage}</Badge>
                </h3>
                <small>{a.areaId}</small>
                <p>{COVERAGE_TEXT[a.coverage]}</p>
                <p>
                  已配置 {a.configuredSources} · 启用 {a.enabledSources} · RECENT {a.recentSources}{' '}
                  · 有资格 {a.eligibleSources}
                </p>
                <p>
                  覆盖指标：{a.coveredMetrics.join(' / ') || '无'} · 缺失指标：
                  {a.missingMetrics.join(' / ') || '无'}
                </p>
              </article>
            ))}
          </div>
          {!overview && (
            <div className="nm-table-wrap">
              <table className="nm-table">
                <caption>实际来源 · 意图 / 接收健康 / 判断资格分别显示</caption>
                <thead>
                  <tr>
                    <th>来源 / 区域</th>
                    <th>指标 / 类别</th>
                    <th>配置意图 / 修订 / 周期</th>
                    <th>接收健康 / 判断资格</th>
                    <th>本周期观测 / 接收</th>
                  </tr>
                </thead>
                <tbody>
                  {data.configuration.sources.map((s) => {
                    const runtime = data.network.sources.find((r) => r.sourceId === s.sourceId);
                    return (
                      <tr key={s.sourceId} data-nm-network-source={s.sourceId}>
                        <td>
                          {s.displayName}
                          <small>
                            {s.sourceId} / {s.areaId}
                          </small>
                        </td>
                        <td>
                          {s.metric} · {s.unit}
                          <small>{s.category}</small>
                        </td>
                        <td>
                          {s.enabled ? '启用' : '停用'}
                          <small>
                            revision {s.revision} · epoch{' '}
                            <span data-nm-source-epoch>{s.epoch}</span>
                          </small>
                          <small>
                            启用时间{' '}
                            {s.epoch === 0
                              ? '尚未启用'
                              : (s.enabledAt ?? '原六来源迁移：启用时间未知')}
                          </small>
                        </td>
                        <td>
                          {runtime?.receiveHealth ?? '未知'}
                          <small>{runtime?.eligible ? '有资格参与判断' : '无资格参与判断'}</small>
                          <small>正常样本 {runtime?.normalSamples ?? '未知'}</small>
                        </td>
                        <td>
                          {runtime?.latestObservedAt ?? '本周期无观测'}
                          <small>{runtime?.latestReceivedAt ?? '本周期无接收'}</small>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}
